/**
 * <ActivityRowSkeleton> — la forme de `<ActivityRow>` pendant un chargement.
 *
 * Posé partout où des activités arrivent : les squelettes des trois pages d'activités (« À faire »,
 * les archivées, les terminées) et le pied d'une liste dont la suite est en route (« Voir plus »).
 * La chip de matière, le titre et le chapitre, puis ce que la ligne montre à droite : le
 * chronomètre d'une activité à faire, ou la date et le statut d'une activité faite.
 *
 * La chip de matière est `<SubjectBadgeSkeleton>`, à la boîte de `<SubjectBadge>` : une retouche
 * de la chip se voit ici sans recopie.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { SubjectBadgeSkeleton } from "../../atoms/SubjectBadge"
import { CardListRow } from "../CardList"

export type ActivityRowSkeletonProps = {
    /** La barre du titre, en px — chaque ligne a la sienne, comme des titres qui diffèrent. */
    titleWidth?: number
    /** Une activité faite : la date et le statut à droite, au lieu du chronomètre. */
    done?: boolean
    /** Le filet du dessus, quand la ligne suit une autre ligne squelette dans la même carte. */
    divided?: boolean
}

export function ActivityRowSkeleton(props: ActivityRowSkeletonProps) {
    const { titleWidth = 150, done = false, divided = false } = props

    return (
        <CardListRow divided={divided}>
            <Row gap="3" align="center">
                <SubjectBadgeSkeleton />
                <Stack gap="1" style={styles.body}>
                    <Skeleton textSize="text-md" width={titleWidth} />
                    <Skeleton textSize="text-sm" width={140} />
                </Stack>
                {done ? (
                    <Stack gap="1" align="end">
                        <Skeleton textSize="text-xs" width={56} />
                        <Skeleton shape="chip" width={60} />
                    </Stack>
                ) : (
                    <Skeleton shape="chip" width={64} />
                )}
            </Row>
        </CardListRow>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flex: 1, flexShrink: 1 },
}))
